/**
 * Fail-soft telemetry client for marketing-demo.
 *
 * Ad blockers commonly abort network requests whose URL contains "analytics".
 * In Vite dev, source modules are served by path — so a static import of
 * `@playcastdotio/Analytics` (or anything under `/analytics/`) can fail the
 * entire ES module graph and white-screen the app.
 *
 * marketing-demo's Vite config aliases `@playcastdotio/Analytics` → this file,
 * so every static import (including from MobileGamepad etc.) gets this wrapper.
 * The real package is loaded only via `@playcastdotio/AnalyticsImpl` (dynamic),
 * and degrades to no-ops when that fetch is blocked.
 */

import type { ExitDetectionConfig } from '@playcastdotio/AnalyticsImpl';

type AnalyticsModule = typeof import('@playcastdotio/AnalyticsImpl');
type SendAnalyticsArgs = Parameters<AnalyticsModule['sendAnalytics']>;

let analyticsModule: AnalyticsModule | null = null;
let loadFailed = false;
let loadPromise: Promise<AnalyticsModule | null> | null = null;

const loadAnalyticsModule = (): Promise<AnalyticsModule | null> => {
  if (analyticsModule) return Promise.resolve(analyticsModule);
  if (loadFailed) return Promise.resolve(null);
  if (!loadPromise) {
    loadPromise = import('@playcastdotio/AnalyticsImpl')
      .then((mod) => {
        analyticsModule = mod;
        return mod;
      })
      .catch((err) => {
        loadFailed = true;
        console.warn(
          '[telemetry] Analytics module unavailable (blocked or failed to load); continuing without telemetry',
          err,
        );
        return null;
      });
  }
  return loadPromise;
};

// Warm the dynamic import as early as possible without blocking boot.
void loadAnalyticsModule();

export const sendAnalytics = async (...args: SendAnalyticsArgs): Promise<string> => {
  const eventName = args[0]?.name ?? 'unknown';
  const mod = await loadAnalyticsModule();
  if (!mod) {
    return `analytics unavailable: ${eventName}`;
  }
  try {
    return await mod.sendAnalytics(...args);
  } catch (err) {
    console.warn('[telemetry] sendAnalytics failed:', eventName, err);
    return `analytics failed: ${eventName}`;
  }
};

/**
 * Mirrors Analytics.initializeExitDetection. If the package never loads,
 * returns a no-op cleanup so callers stay sync-safe.
 */
export const initializeExitDetection = (config: ExitDetectionConfig): (() => void) => {
  let realCleanup: (() => void) | null = null;
  let cancelled = false;

  void loadAnalyticsModule().then((mod) => {
    if (cancelled || !mod) return;
    try {
      realCleanup = mod.initializeExitDetection(config);
    } catch (err) {
      console.warn('[telemetry] initializeExitDetection failed:', err);
    }
  });

  return () => {
    cancelled = true;
    realCleanup?.();
  };
};

export type {
  DeviceHandoffMethod,
  ExitDetectionConfig,
  ExitMetadata,
  ExitReason,
  PlaycastAnalyticsEvent,
  PlaycastAnalyticsHostProspectEmailed,
  PlaycastAnalyticsOptions,
  PlaycastAnalyticsOverlayDisplayed,
  PlaycastAnalyticsPageViewed,
  PlaycastAnalyticsPlayerFrameratePanicked,
  PlaycastAnalyticsPlayerInviteAccepted,
  PlaycastAnalyticsPlayerInviteViewed,
  PlaycastAnalyticsWebRtcStatsGathered,
  VisibilityReason,
} from '@playcastdotio/AnalyticsImpl';
